import { ReactNode, useEffect, useState } from 'react';
import {
  Link as RouterLink,
  matchRoutes,
  useLocation,
  useNavigate,
  useParams,
  useRoutes,
  useSearchParams,
} from 'react-router-dom';
import {
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import GroupIcon from '@material-ui/icons/Group';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import CodeIcon from '@material-ui/icons/Code';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import SearchIcon from '@material-ui/icons/Search';
import LinkIcon from '@material-ui/icons/Link';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import {
  attachComponentData,
  useApi,
  useElementFilter,
  useRouteRefParams,
} from '@backstage/core-plugin-api';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import {
  InspectEntityDialog,
  UnregisterEntityDialog,
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
  getEntitySourceLocation,
  useAsyncEntity,
  useStarredEntity,
} from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityDeletePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  DEFAULT_NAMESPACE,
  Entity,
  RELATION_OWNED_BY,
} from '@backstage/catalog-model';
import { FluxButton, FluxChip, MONO_FONT, fluxTokens } from '../../flux';
import {
  entityIcon,
  humanizeName,
  kindLabels,
  lifecycleTone,
  shortRef,
} from '../index-page/presentation';

const dataKey = 'plugin.catalog.entityLayoutRoute';

type RouteProps = {
  path: string;
  title: string;
  children: JSX.Element;
  if?: (entity: Entity) => boolean;
  tabProps?: Record<string, unknown>;
};

const Route: (props: RouteProps) => null = () => null;
attachComponentData(Route, dataKey, true);
attachComponentData(Route, 'core.gatherMountPoints', true);

type SubRoute = { path: string; title: string; children: JSX.Element };

const useStyles = makeStyles(theme => {
  const t = fluxTokens(theme);
  return {
    page: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: t.pageBg,
      color: t.text,
    },
    top: {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      background: t.panelBg,
      borderBottom: `1px solid ${t.border}`,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      minHeight: 56,
      padding: '8px 20px',
      flexWrap: 'wrap',
    },
    crumbs: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12.5,
      color: t.textMuted,
      whiteSpace: 'nowrap',
      '& a': {
        color: t.textMuted,
        textDecoration: 'none',
        '&:hover': { color: t.text },
      },
    },
    crumbSep: { opacity: 0.6 },
    identity: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minWidth: 0,
      flex: 1,
    },
    iconTile: {
      flexShrink: 0,
      width: 30,
      height: 30,
      borderRadius: 8,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: t.accent,
      background: t.accentBg,
      border: `1px solid ${t.accentBorder}`,
      '& svg': { fontSize: 17 },
    },
    title: {
      margin: 0,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: '-0.01em',
      color: t.text,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    star: {
      padding: 4,
      color: t.textMuted,
      '& svg': { fontSize: 18 },
    },
    starOn: { color: '#f5b82e' },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginLeft: 'auto',
      flexWrap: 'wrap',
    },
    metaItem: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 11,
      color: t.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      fontWeight: 600,
    },
    divider: {
      width: 1,
      height: 22,
      background: t.border,
      margin: '0 2px',
    },
    menuBtn: {
      width: 32,
      height: 32,
      borderRadius: 8,
      border: `1px solid ${t.borderStrong}`,
      color: t.textSecondary,
      '& svg': { fontSize: 18 },
    },
    description: {
      padding: '0 20px 8px 60px',
      marginTop: -4,
      fontSize: 12,
      color: t.textSecondary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    tabs: {
      display: 'flex',
      gap: 2,
      padding: '0 14px',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
    tab: {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      height: 38,
      padding: '0 12px',
      fontSize: 12.5,
      fontWeight: 500,
      color: t.textSecondary,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      borderRadius: '6px 6px 0 0',
      transition: 'color 0.15s ease, background-color 0.15s ease',
      '&:hover': { color: t.text, background: t.surfaceHover },
    },
    tabActive: {
      color: t.text,
      fontWeight: 600,
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 10,
        right: 10,
        bottom: 0,
        height: 2,
        borderRadius: 2,
        background: t.accentStrong,
      },
    },
    progress: {
      height: 2,
      background: 'transparent',
      '& .MuiLinearProgress-bar': { background: t.accentStrong },
    },
    content: {
      flex: 1,
      padding: '16px 20px 24px',
      '& .MuiGrid-spacing-xs-3': {
        width: 'calc(100% + 16px)',
        margin: -8,
        '& > .MuiGrid-item': { padding: 8 },
      },
      '& .MuiCard-root, & .MuiPaper-outlined': {
        borderRadius: 10,
        border: `1px solid ${t.border}`,
        background: t.surface,
        boxShadow: 'none',
        backgroundImage: 'none',
      },
      '& .MuiCardHeader-root': {
        padding: '12px 16px',
      },
      '& .MuiCardHeader-title': {
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        color: t.text,
      },
      '& .MuiCardHeader-subheader': {
        fontSize: 12,
        color: t.textSecondary,
      },
      '& .MuiCardHeader-action': {
        marginTop: 0,
        marginRight: 0,
        alignSelf: 'center',
        '& .MuiIconButton-root': { padding: 6 },
        '& svg': { fontSize: 18 },
      },
      '& .MuiCardContent-root': {
        padding: '12px 16px',
        fontSize: 13,
      },
      '& .MuiCardActions-root': {
        padding: '8px 16px',
        borderTop: `1px solid ${t.border}`,
      },
      '& .MuiCard-root .MuiDivider-root': {
        backgroundColor: t.border,
      },
      '& .MuiCard-root .MuiTypography-h2': {
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: t.textMuted,
        marginBottom: 4,
      },
      '& .MuiCard-root .MuiTypography-body2': {
        fontSize: 13,
      },
      '& .MuiCard-root .MuiChip-root': {
        height: 22,
        borderRadius: 6,
        fontFamily: MONO_FONT,
        fontSize: 11,
        background: t.chipBg,
        border: `1px solid ${t.chipBorder}`,
        color: t.text,
        margin: '2px 4px 2px 0',
      },
      '& .MuiCard-root .MuiChip-label': { padding: '0 7px' },
      '& .MuiAlert-root': {
        borderRadius: 10,
        padding: '6px 14px',
        fontSize: 12.5,
        alignItems: 'center',
      },
      '& .MuiAlert-standardWarning': {
        color: t.warning,
        background: t.warningBg,
        border: `1px solid ${t.dark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.35)'}`,
      },
      '& .MuiAlert-standardError': {
        color: t.danger,
        background: t.dangerBg,
        border: `1px solid ${t.dark ? 'rgba(239,68,68,0.3)' : 'rgba(239,68,68,0.3)'}`,
      },
      '& .MuiAlert-icon': { padding: '4px 0', '& svg': { fontSize: 18 } },
      '& .MuiAlert-message': { padding: '4px 0' },
      '& .MuiTableCell-root': {
        fontSize: 12.5,
        borderBottomColor: t.border,
      },
      '& .MuiTableCell-head': {
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: t.textMuted,
      },
      '& .MuiTab-root': {
        minHeight: 36,
        fontSize: 12.5,
        textTransform: 'none',
      },
      '& .MuiTabs-root': { minHeight: 36 },
    },
    menuPaper: {
      background: t.surface,
      border: `1px solid ${t.borderStrong}`,
      borderRadius: 10,
      minWidth: 220,
    },
    menuItem: {
      fontSize: 12.5,
      minHeight: 34,
      '& .MuiListItemIcon-root': { minWidth: 30, color: t.textMuted },
      '& svg': { fontSize: 17 },
    },
    menuDanger: {
      color: t.danger,
      '& .MuiListItemIcon-root': { color: t.danger },
    },
    notFound: { margin: 20 },
    notice: {
      padding: '10px 14px',
      borderRadius: 10,
      fontSize: 12.5,
      color: t.warning,
      background: t.warningBg,
    },
    noticeError: {
      margin: 20,
      color: t.danger,
      background: t.dangerBg,
    },
  };
});

function toRouteObjects(subRoutes: SubRoute[]) {
  return subRoutes
    .map(({ path, children }) => ({
      caseSensitive: false,
      path: `${path}/*`,
      element: children,
    }))
    .sort((a, b) =>
      b.path.replace(/\/\*$/, '').localeCompare(a.path.replace(/\/\*$/, '')),
    );
}

function useSelectedIndex(subRoutes: SubRoute[]) {
  const params = useParams();
  let currentRoute = params['*'] ?? '';
  if (!currentRoute.startsWith('/')) currentRoute = `/${currentRoute}`;
  const [matched] = matchRoutes(toRouteObjects(subRoutes), currentRoute) ?? [];
  const index = matched
    ? subRoutes.findIndex(r => `${r.path}/*` === matched.route.path)
    : 0;
  return index === -1 ? 0 : index;
}

function useSelectedSubRoute(subRoutes: SubRoute[]) {
  const element = useRoutes(toRouteObjects(subRoutes)) ?? subRoutes[0]?.children;
  return { index: useSelectedIndex(subRoutes), element };
}

function useOwnerTitle(ref?: string) {
  const catalogApi = useApi(catalogApiRef);
  const [title, setTitle] = useState<string | undefined>();
  useEffect(() => {
    setTitle(undefined);
    if (!ref) return;
    catalogApi
      .getEntityByRef(ref)
      .then(owner => {
        const profile = (owner?.spec as any)?.profile;
        setTitle(profile?.displayName ?? owner?.metadata.title);
      })
      .catch(() => undefined);
  }, [catalogApi, ref]);
  return title ?? (ref ? humanizeName(shortRef(ref)) : undefined);
}

const StarToggle = ({ entity }: { entity: Entity }) => {
  const classes = useStyles();
  const { isStarredEntity, toggleStarredEntity } = useStarredEntity(entity);
  return (
    <Tooltip title={isStarredEntity ? 'Quitar de favoritos' : 'Marcar como favorito'}>
      <IconButton
        size="small"
        className={`${classes.star} ${isStarredEntity ? classes.starOn : ''}`}
        onClick={toggleStarredEntity}
      >
        {isStarredEntity ? <StarIcon /> : <StarBorderIcon />}
      </IconButton>
    </Tooltip>
  );
};

const EntityHeader = ({
  entity,
  fallbackName,
  kind,
  onInspect,
  onUnregister,
}: {
  entity?: Entity;
  fallbackName: string;
  kind: string;
  onInspect: () => void;
  onUnregister: () => void;
}) => {
  const classes = useStyles();
  const scmIntegrations = useApi(scmIntegrationsApiRef);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { allowed: canDelete } = useEntityPermission(catalogEntityDeletePermission);

  const ownerRef = entity?.relations?.find(r => r.type === RELATION_OWNED_BY)?.targetRef;
  const ownerTitle = useOwnerTitle(ownerRef);
  const labels = kindLabels(kind);
  const spec = (entity?.spec ?? {}) as Record<string, any>;
  const namespace = entity?.metadata.namespace;
  const name = entity?.metadata.title ?? entity?.metadata.name ?? fallbackName;
  const Icon = entity ? entityIcon(entity) : null;

  let sourceUrl: string | undefined;
  try {
    sourceUrl = entity ? getEntitySourceLocation(entity, scmIntegrations)?.locationTargetUrl : undefined;
  } catch {
    sourceUrl = undefined;
  }
  const hasDocs = Boolean(entity?.metadata.annotations?.['backstage.io/techdocs-ref']);
  const ownerParams = ownerRef ? entityRouteParams(ownerRef) : undefined;
  const ownerHref = ownerParams
    ? `/catalog/${ownerParams.namespace}/${ownerParams.kind}/${ownerParams.name}`
    : undefined;

  return (
    <>
      <div className={classes.header}>
        <nav className={classes.crumbs} aria-label="breadcrumb">
          <RouterLink to="/catalog">Catálogo</RouterLink>
          <span className={classes.crumbSep}>›</span>
          <RouterLink
            to={`/catalog?filters%5Bkind%5D=${encodeURIComponent(kind.toLocaleLowerCase('en-US'))}&filters%5Buser%5D=all`}
          >
            {labels.title}
          </RouterLink>
          <span className={classes.crumbSep}>›</span>
        </nav>
        <div className={classes.identity}>
          {Icon && (
            <span className={classes.iconTile}>
              <Icon />
            </span>
          )}
          <h1 className={classes.title} title={name}>
            {name}
          </h1>
          {entity && <StarToggle entity={entity} />}
          <FluxChip tone="accent">
            {kind.toLocaleLowerCase('en-US')}
            {spec.type ? ` · ${String(spec.type).toLocaleLowerCase('en-US')}` : ''}
          </FluxChip>
          {namespace && namespace !== DEFAULT_NAMESPACE && (
            <FluxChip>ns: {namespace}</FluxChip>
          )}
        </div>
        {entity && (
          <div className={classes.meta}>
            {ownerRef && (
              <span className={classes.metaItem}>
                Owner
                <FluxChip
                  to={ownerHref}
                  icon={ownerRef.startsWith('user:') ? <PersonOutlineIcon /> : <GroupIcon />}
                  title={ownerRef}
                >
                  {ownerTitle}
                </FluxChip>
              </span>
            )}
            {spec.lifecycle && (
              <span className={classes.metaItem}>
                Lifecycle
                <FluxChip tone={lifecycleTone(spec.lifecycle)}>{String(spec.lifecycle)}</FluxChip>
              </span>
            )}
            <span className={classes.divider} />
            {sourceUrl && (
              <FluxButton
                size="small"
                icon={<CodeIcon />}
                onClick={() => window.open(sourceUrl, '_blank', 'noopener')}
              >
                Source
              </FluxButton>
            )}
            {hasDocs && (
              <FluxButton size="small" icon={<DescriptionOutlinedIcon />} to="docs">
                Docs
              </FluxButton>
            )}
            <IconButton
              className={classes.menuBtn}
              aria-label="Más acciones"
              onClick={e => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ className: classes.menuPaper }}
            >
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  setMenuAnchor(null);
                  onInspect();
                }}
              >
                <ListItemIcon>
                  <SearchIcon />
                </ListItemIcon>
                Inspeccionar entidad
              </MenuItem>
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  setMenuAnchor(null);
                  navigator.clipboard?.writeText(window.location.href);
                }}
              >
                <ListItemIcon>
                  <LinkIcon />
                </ListItemIcon>
                Copiar URL
              </MenuItem>
              <MenuItem
                className={`${classes.menuItem} ${classes.menuDanger}`}
                disabled={!canDelete}
                onClick={() => {
                  setMenuAnchor(null);
                  onUnregister();
                }}
              >
                <ListItemIcon>
                  <DeleteOutlineIcon />
                </ListItemIcon>
                Desregistrar entidad
              </MenuItem>
            </Menu>
          </div>
        )}
      </div>
      {entity?.metadata.description && (
        <div className={classes.description} title={entity.metadata.description}>
          {entity.metadata.description}
        </div>
      )}
    </>
  );
};

const Tabs = ({ routes, selected }: { routes: SubRoute[]; selected: number }) => {
  const classes = useStyles();
  return (
    <nav className={classes.tabs}>
      {routes.map((route, i) => {
        const to = route.path.replace(/\/\*$/, '').replace(/^\//, '');
        return (
          <RouterLink
            key={route.path}
            to={to || '.'}
            className={`${classes.tab} ${i === selected ? classes.tabActive : ''}`}
          >
            {route.title}
          </RouterLink>
        );
      })}
    </nav>
  );
};

const RoutedHeaderTabs = ({ routes }: { routes: SubRoute[] }) => (
  <Tabs routes={routes} selected={useSelectedIndex(routes)} />
);

const RoutedBody = ({ routes }: { routes: SubRoute[] }) => {
  const classes = useStyles();
  const { index, element } = useSelectedSubRoute(routes);
  useEffect(() => {
    const title = routes[index]?.title;
    if (title) document.title = `${title} | FluxStage`;
  }, [routes, index]);
  return <main className={classes.content}>{element}</main>;
};

export const FluxEntityLayout = (props: { children?: ReactNode; NotFoundComponent?: ReactNode }) => {
  const classes = useStyles();
  const { kind, name } = useRouteRefParams(entityRouteRef);
  const { entity, loading, error } = useAsyncEntity();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [unregisterOpen, setUnregisterOpen] = useState(false);

  const routes = useElementFilter(
    props.children,
    elements =>
      elements
        .selectByComponentData({
          key: dataKey,
          withStrictError: 'Child of FluxEntityLayout must be a FluxEntityLayout.Route',
        })
        .getElements<RouteProps>()
        .flatMap(({ props: routeProps }) => {
          if (!entity) return [];
          if (routeProps.if && !routeProps.if(entity)) return [];
          return [
            {
              path: routeProps.path,
              title: routeProps.title,
              children: routeProps.children,
            },
          ];
        }),
    [entity],
  );

  useEffect(() => setUnregisterOpen(false), [location.pathname]);

  const inspectTab = searchParams.get('inspect');

  return (
    <div className={classes.page}>
      <div className={classes.top}>
        <EntityHeader
          entity={entity}
          fallbackName={name}
          kind={entity?.kind ?? kind}
          onInspect={() =>
            setSearchParams(prev => {
              const next = new URLSearchParams(prev);
              next.set('inspect', '');
              return next;
            })
          }
          onUnregister={() => setUnregisterOpen(true)}
        />
        {loading && <LinearProgress className={classes.progress} />}
        {entity && routes.length > 0 && <RoutedHeaderTabs routes={routes} />}
      </div>
      {entity && <RoutedBody routes={routes} />}
      {error && (
        <div className={`${classes.notice} ${classes.noticeError}`}>
          {error.toString()}
        </div>
      )}
      {!loading && !error && !entity && (
        <div className={classes.notFound}>
          {props.NotFoundComponent ?? (
            <div className={classes.notice}>
              No se encontró la entidad {kind} “{name}” en el catálogo.
            </div>
          )}
        </div>
      )}
      {typeof inspectTab === 'string' && entity && (
        <InspectEntityDialog
          entity={entity}
          initialTab={(inspectTab || undefined) as any}
          onSelect={tab =>
            setSearchParams(prev => {
              const next = new URLSearchParams(prev);
              next.set('inspect', tab);
              return next;
            })
          }
          open
          onClose={() =>
            setSearchParams(prev => {
              const next = new URLSearchParams(prev);
              next.delete('inspect');
              return next;
            })
          }
        />
      )}
      {entity && (
        <UnregisterEntityDialog
          open={unregisterOpen}
          entity={entity}
          onConfirm={() => {
            setUnregisterOpen(false);
            navigate('/catalog');
          }}
          onClose={() => setUnregisterOpen(false)}
        />
      )}
    </div>
  );
};

FluxEntityLayout.Route = Route;
